import { useQueryGetBorrowerExclusionReasons } from '@oaknorth/api-polaris';
import {
  Box,
  BreadcrumbCurrentLink,
  BreadcrumbRoot,
  Heading,
  PageHeader,
  PageTitle,
  Result,
  Skeleton,
  Status,
  UnorderedList,
  VStack,
} from '@oaknorth/component-core-v2';
import { useTranslation } from '@oaknorth/util-intl';
import { ResponsivePageContainer } from '@oaknorth/util-responsive';
import { isErrorWithStatus } from '@oaknorth/util-rest';
import { BreadcrumbLinkItem, Helmet, useRequiredParams } from '@oaknorth/util-routing';

import { SectorWithCode } from '../BorrowerAudit/ExcludedBorrowersTable';

import { BorrowerDetails } from './BorrowerDetails';
import { ReasonDetails } from './ReasonDetails';

export function ExcludedBorrowerOverview() {
  const { t } = useTranslation();
  const { borrowerId } = useRequiredParams('borrowerId');
  const { data, isLoading, isError, error } = useQueryGetBorrowerExclusionReasons({
    path: { legalEntityId: borrowerId },
  });

  if (isError) {
    return (
      <Result
        status={Status.ERROR}
        title={
          isErrorWithStatus(error, 404)
            ? t`explainability.missingBorrowerEmptyState.title`
            : t`explainability.borrower.unknownError`
        }
      />
    );
  }

  const borrowerOverview = data?.borrower_overview;
  const { name: borrowerName, naics_info: naicInfo } = borrowerOverview ?? {};

  return (
    <ResponsivePageContainer
      nav={
        <BreadcrumbRoot>
          <BreadcrumbLinkItem to="../" isHomePage>{t`diagnostics.title`}</BreadcrumbLinkItem>
          <BreadcrumbLinkItem to="../excluded-borrowers">{t`diagnostics.excludedBorrowers.heading`}</BreadcrumbLinkItem>
          {borrowerName && <BreadcrumbCurrentLink> {borrowerName} </BreadcrumbCurrentLink>}
        </BreadcrumbRoot>
      }
    >
      <PageHeader>
        <Skeleton loading={isLoading} width="100%">
          <Helmet title={borrowerName} />
          <PageTitle>{borrowerName}</PageTitle>
          <SectorWithCode name={naicInfo?.name} code={naicInfo?.code} />
        </Skeleton>
      </PageHeader>
      <VStack alignItems="stretch" gap={8}>
        <BorrowerDetails borrower={borrowerOverview} isLoading={isLoading} />
        {/* eslint-disable-next-line i18next/no-literal-string -- non user facing */}
        <Box as="section" aria-labelledby="exclusion-reasons-header">
          <Heading as="h2" id="exclusion-reasons-header">{t`diagnostics.excludedBorrowerOverview.heading`}</Heading>
          {/* eslint-disable-next-line i18next/no-literal-string -- Remove the bullet style, non user facing */}
          <UnorderedList listStyleType="none" aria-labelledby="exclusion-reasons-header">
            {data?.exclusion_reasons.map((reason) => (
              <ReasonDetails key={reason.reason} reason={reason} borrowerId={borrowerId} borrowerName={borrowerName} />
            ))}
          </UnorderedList>
        </Box>
      </VStack>
    </ResponsivePageContainer>
  );
}
